import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import type {
  AcquisitionCohortGroup,
  AttributionLayer,
  CurrencyAmounts,
} from '@/services/adminMetricsService';
import { cn } from '@/lib/utils';

interface AcquisitionCohortTableProps {
  groups: AcquisitionCohortGroup[];
  formatCents: (cents: number, currency: string) => string;
}

// Month-4 is the first full-price month (months 1-3 are the 50%-off window)
// — the churn cliff §7 names. The highlight is positional, not data-driven.
const MONTH_4 = 4;
// Kill-trigger threshold (§4/MARKETING.md): cohort churn > 7-8%/month = kill.
const CHURN_DESTRUCTIVE_THRESHOLD = 7;

function layerLabelKey(layer: AttributionLayer): string {
  return `admin.metrics.acquisition.layer.${layer}`;
}

/**
 * Commission cell for a `code`-layer row. `commission_accrued_cents` is a
 * zero-filled per-currency dict (mirrors the `mrr_cents` convention) — most
 * rows only ever populate one currency, but a code can in principle attract
 * signups in both markets. Renders "—" when nothing accrued yet.
 */
function renderCommission(
  amounts: CurrencyAmounts,
  formatCents: (cents: number, currency: string) => string,
  t: (key: string) => string
): ReactNode {
  const nonZero = (Object.entries(amounts) as Array<[string, number]>).filter(
    ([, cents]) => cents > 0
  );
  if (nonZero.length === 0) {
    return <span className="text-muted-foreground/40">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{nonZero.map(([currency, cents]) => formatCents(cents, currency)).join(' + ')}</span>
      <Badge variant="outline" className="text-xs">
        {t('admin.metrics.acquisition.aprox')}
      </Badge>
    </span>
  );
}

/**
 * Acquisition cohort matrix — CohortMatrix (F062) sibling. Row groups are
 * attribution keys (promo code / ref / self-report source / organic), each
 * expanding into one sub-row per signup-cohort month. Columns are the shared
 * funnel counts plus churn-by-month offsets. Monochrome; existing semantic
 * tokens only (no new colors).
 *
 * Mobile: overflow-x-auto (sanctioned exception for dense data grids per
 * §21.7 — mirrors CohortMatrix). The container must NEVER be hidden on any
 * viewport; the page body itself never scrolls horizontally.
 */
export function AcquisitionCohortTable({ groups, formatCents }: AcquisitionCohortTableProps) {
  const { t } = useTranslation();

  if (groups.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('admin.metrics.acquisition.empty')}</p>;
  }

  const hasCodeGroup = groups.some((group) => group.layer === 'code');

  const maxMonth = groups.reduce((groupMax, group) => {
    const groupCohortMax = group.cohorts.reduce((cohortMax, cohort) => {
      const offsets = Object.keys(cohort.churn_by_month).map(Number);
      const highest = offsets.length > 0 ? Math.max(...offsets) : 0;
      return Math.max(cohortMax, highest);
    }, 0);
    return Math.max(groupMax, groupCohortMax);
  }, 0);

  const monthColumns = Array.from({ length: maxMonth }, (_, i) => i + 1);
  const colSpan = 5 + monthColumns.length + (hasCodeGroup ? 1 : 0);

  return (
    <div data-testid="acquisition-cohort-table" className="overflow-x-auto">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="py-1.5 pr-3 text-left font-medium text-muted-foreground whitespace-nowrap">
              {t('admin.metrics.acquisition.cohort_col')}
            </th>
            <th className="py-1.5 px-2 text-right font-medium text-muted-foreground whitespace-nowrap">
              {t('admin.metrics.acquisition.signups_col')}
            </th>
            <th className="py-1.5 px-2 text-right font-medium text-muted-foreground whitespace-nowrap">
              {t('admin.metrics.acquisition.trials_col')}
            </th>
            <th className="py-1.5 px-2 text-right font-medium text-muted-foreground whitespace-nowrap">
              {t('admin.metrics.acquisition.converted_col')}
            </th>
            <th className="py-1.5 px-2 text-right font-medium text-muted-foreground whitespace-nowrap">
              {t('admin.metrics.acquisition.active_col')}
            </th>
            {monthColumns.map((m) => (
              <th
                key={m}
                data-testid={`month-header-m${m}`}
                className={cn(
                  'py-1.5 px-2 text-right font-medium text-muted-foreground whitespace-nowrap',
                  m === MONTH_4 && 'bg-muted font-medium text-foreground'
                )}
              >
                M{m}
              </th>
            ))}
            {hasCodeGroup && (
              <th className="py-1.5 px-2 text-right font-medium text-muted-foreground whitespace-nowrap">
                {t('admin.metrics.acquisition.commission_col')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const layerLabel = t(layerLabelKey(group.layer));
            const headerLabel =
              group.layer === 'organic' ? layerLabel : `${group.key} (${layerLabel})`;

            return (
              <Fragment key={group.key}>
                <tr
                  data-testid={`acquisition-group-${group.key}`}
                  className="border-t border-border bg-muted/40"
                >
                  <td colSpan={colSpan} className="py-1.5 pr-3 font-medium">
                    {headerLabel}
                  </td>
                </tr>
                {group.cohorts.map((cohort) => (
                  <tr key={`${group.key}-${cohort.cohort}`} className="border-t border-border">
                    <td className="py-1.5 pr-3 pl-4 text-muted-foreground whitespace-nowrap">
                      {cohort.cohort.slice(0, 7)}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{cohort.signups}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{cohort.trials_started}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{cohort.converted}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{cohort.active_now}</td>
                    {monthColumns.map((m) => {
                      const pct = cohort.churn_by_month[m];
                      const hasValue = pct !== undefined;
                      return (
                        <td
                          key={m}
                          data-testid={`churn-cell-${group.key}-${cohort.cohort}-m${m}`}
                          className={cn(
                            'py-1.5 px-2 text-right tabular-nums',
                            m === MONTH_4 && 'bg-muted',
                            hasValue &&
                              pct > CHURN_DESTRUCTIVE_THRESHOLD &&
                              'text-destructive font-medium'
                          )}
                        >
                          {hasValue ? `${pct}%` : '—'}
                        </td>
                      );
                    })}
                    {hasCodeGroup && (
                      <td className="py-1.5 px-2 text-right tabular-nums">
                        {group.layer === 'code' ? (
                          renderCommission(cohort.commission_accrued_cents, formatCents, t)
                        ) : (
                          <span className="text-muted-foreground/40">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
